/**
 * AGameOfAnalogies Component
 *
 * Animated panel covering the score panel area (226×144px at left=18, top=838).
 * Uses pre-rendered AGameOfAssociations SVG components for the split-door halves.
 *
 * Two halves split along a diagonal (built into the SVGs):
 *   Upper (brown #514b44) — slides UP to open, slides IN from above to close
 *   Lower (charcoal #262626) — slides DOWN to open, slides IN from below to close
 *
 * Props:
 *   separated    — true = halves apart (panel open, credit counter visible)
 *                  false = halves together (panel closed, credit counter covered)
 *   slideDuration — animation duration in ms (default 1000).
 *
 * Phase machine:
 *   'closed'  → halves at y=0,       no transition
 *   'opening' → halves at y=±LOWER_H, transition active  (closed → open)
 *   'open'    → halves at y=±LOWER_H, no transition
 *   'closing' → halves at y=0,       transition active  (open → closed)
 */

import React, { useEffect, useState } from 'react';
import AGameOfAssociationsUpper from '../svg/AGameOfAssociationsUpper';
import AGameOfAssociationsLower from '../svg/AGameOfAssociationsLower';

// Panel dimensions — matches BoardBackground charcoal interior rect (18, 838, 226, 144)
const PANEL_WIDTH = 226;
const PANEL_HEIGHT = 144;
const DEFAULT_SLIDE_DURATION = 1000; // ms

// Split geometry from SVG component heights
const UPPER_H = 79.093;         // AGameOfAssociationsUpper rendered height
const LOWER_H = 94.821;         // AGameOfAssociationsLower rendered height

type AGamePhase = 'closed' | 'opening' | 'open' | 'closing';

interface AGameOfAnalogiesProps {
  separated: boolean;
  slideDuration?: number;
  onClosed?: () => void;
  style?: React.CSSProperties;
}

const AGameOfAnalogies: React.FC<AGameOfAnalogiesProps> = ({ separated, slideDuration = DEFAULT_SLIDE_DURATION, onClosed, style }) => {
  // Initialise to match the prop — no mount animation
  const [phase, setPhase] = useState<AGamePhase>(() => separated ? 'open' : 'closed');

  useEffect(() => {
    if (separated && phase === 'closed') {
      // Closed → opening: double-rAF so browser records translateY(0) as computed style
      // before we apply the transition + target translateY(±LOWER_H)
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setPhase('opening');
        });
      });
      return () => { cancelled = true; };
    }
    if (!separated && phase === 'open') {
      // Open → closing: same pattern
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setPhase('closing');
        });
      });
      return () => { cancelled = true; };
    }
  }, [separated, phase]);

  const isOpen = phase === 'open' || phase === 'opening';
  const hasTransition = phase === 'opening' || phase === 'closing';

  // Each half travels just past the clip boundary — 2px margin prevents hairline edge.
  const upperY = isOpen ? -(UPPER_H + 2) : 0;
  const lowerY = isOpen ? (LOWER_H + 2) : 0;

  return (
    <div style={{
      width: PANEL_WIDTH,
      height: PANEL_HEIGHT,
      overflow: 'hidden',
      pointerEvents: 'none',
      ...style,
    }}>
        {/* Upper half — brown background with "A GAME OF", slides up to open */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: PANEL_WIDTH,
            height: UPPER_H,
            transform: `translateY(${upperY}px)`,
            transition: hasTransition ? `transform ${slideDuration}ms ease-in-out` : 'none',
          }}
          onTransitionEnd={(e) => {
            if (e.propertyName === 'transform') {
              setPhase(prev => {
                if (prev === 'opening') return 'open';
                if (prev === 'closing') {
                  onClosed?.();
                  return 'closed';
                }
                return prev;
              });
            }
          }}
        >
          <AGameOfAssociationsUpper
            width={PANEL_WIDTH}
            height={UPPER_H}
            style={{ display: 'block' }}
          />
        </div>

        {/* Lower half — charcoal background with "ASSOCIATIONS", slides down to open */}
        <div style={{
          position: 'absolute',
          top: PANEL_HEIGHT - LOWER_H,
          left: 0,
          width: PANEL_WIDTH,
          height: LOWER_H,
          transform: `translateY(${lowerY}px)`,
          transition: hasTransition ? `transform ${slideDuration}ms ease-in-out` : 'none',
        }}>
          <AGameOfAssociationsLower
            width={PANEL_WIDTH}
            height={LOWER_H}
            style={{ display: 'block' }}
          />
        </div>
    </div>
  );
};

export default AGameOfAnalogies;
